import type { ManagerialDashboardFinance } from '@/types/api'

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/
const amount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
const count = (value: unknown): value is number =>
  amount(value) && Number.isInteger(value)

export function isExecutiveFinance(value: unknown): value is ManagerialDashboardFinance {
  if (typeof value !== 'object' || value === null) return false
  const data = value as Record<string, unknown>
  if (
    data.marketing_recorded !== undefined &&
    data.cac !== null &&
    (data.marketing_recorded !== true ||
      !count(data.new_customers) ||
      data.new_customers === 0)
  ) {
    return false
  }
  return (
    typeof data.reference_month === 'string' &&
    MONTH.test(data.reference_month) &&
    amount(data.marketing_spend) &&
    (data.marketing_recorded === undefined ||
      typeof data.marketing_recorded === 'boolean') &&
    count(data.new_customers) &&
    (data.cac === null || amount(data.cac)) &&
    amount(data.undated_expenses) &&
    count(data.unconverted_percentage_entries) &&
    Array.isArray(data.points) &&
    data.points.length <= 12 &&
    data.points.every((point: unknown) => {
      if (typeof point !== 'object' || point === null) return false
      const row = point as Record<string, unknown>
      return (
        typeof row.month === 'string' &&
        MONTH.test(row.month) &&
        amount(row.revenue) &&
        amount(row.expenses)
      )
    })
  )
}

export function formatFinancialMonth(month: string): string {
  const [year, value] = month.split('-').map(Number)
  return new Intl.DateTimeFormat('pt-BR', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, value - 1, 1)))
}

export const INVESTOR_METRICS = [
  {
    id: 'cac',
    label: 'CAC',
    name: 'Custo de Aquisição de Cliente',
    group: 'operational',
    description:
      'Investimento mensal registrado em marketing e aquisição dividido pelas novas instituições. Uma instituição é adquirida no mês da sua primeira venda paga reconhecida. Sem lançamento de marketing ou sem novas instituições, o CAC não é calculado. Zero só é válido com gasto explicitamente registrado como zero e pelo menos uma aquisição. Inclua todos os gastos destinados à aquisição nessa categoria.',
    pending: 'Aguardando dados de aquisição',
  },
  {
    id: 'ltv',
    label: 'LTV',
    name: 'Valor do Tempo de Vida do Cliente',
    group: 'operational',
    description:
      'Receita esperada da plataforma durante o relacionamento com uma instituição. Requer histórico de permanência e um modelo de projeção validado.',
    pending:
      'Definir modelo de permanência e projeção. A receita histórica observada é exibida nas medições gerenciais.',
  },
  {
    id: 'ltv-cac',
    label: 'LTV/CAC',
    name: 'Retorno sobre aquisição',
    group: 'operational',
    description:
      'LTV dividido pelo CAC. Referência de acompanhamento: pelo menos 3x. Com CAC zero ou LTV ainda não mensurado, a relação não é calculada.',
    pending:
      'Definir LTV e aquisição da mesma coorte. Referência: ≥ 3x · sem base de cálculo',
  },
  {
    id: 'churn',
    label: 'Churn Rate',
    name: 'Taxa de Cancelamento',
    group: 'operational',
    description:
      'Percentual de instituições ou receita recorrente perdidas em um período. Exige uma base inicial e eventos de cancelamento. Ausência de vendas não é tratada como cancelamento.',
    pending:
      'Registrar início e cancelamento dos contratos. A perda de atividade de vendas é medida nas medições gerenciais.',
  },
  {
    id: 'nps',
    label: 'NPS',
    name: 'Net Promoter Score',
    group: 'operational',
    description:
      'Percentual de promotores (notas 9 e 10) menos o de detratores (0 a 6) em pesquisa de recomendação. Varia de −100 a 100 e não é uma porcentagem de conversão.',
    pending: 'Coletar notas de recomendação de 0 a 10, com data e população da pesquisa.',
  },
  {
    id: 'mrr',
    label: 'MRR',
    name: 'Receita Mensal Recorrente',
    group: 'financial',
    description:
      'Receita mensal previsível de assinaturas ou contratos recorrentes ativos. Taxas transacionais e vendas avulsas não são classificadas automaticamente como MRR.',
    pending:
      'Cadastrar contratos ou assinaturas recorrentes com valor, vigência e cancelamento.',
  },
  {
    id: 'arr',
    label: 'ARR',
    name: 'Receita Anual Recorrente',
    group: 'financial',
    description:
      'MRR multiplicado por 12. Requer uma base válida de receita recorrente; não é a soma das vendas do ano.',
    pending: 'Depende da mensuração do MRR',
  },
  {
    id: 'burn-rate',
    label: 'Burn Rate',
    name: 'Taxa de Queima de Caixa',
    group: 'financial',
    description:
      'Consumo líquido mensal de caixa operacional. Requer entradas e saídas efetivas de caixa. A diferença entre receita e despesas registradas por competência não é apresentada como queima de caixa.',
    pending: 'Registrar recebimentos e pagamentos efetivos, com data de liquidação.',
  },
  {
    id: 'runway',
    label: 'Runway',
    name: 'Meses de Caixa Disponível',
    group: 'financial',
    description:
      'Caixa disponível dividido pelo Burn Rate mensal positivo. Requer saldo de caixa e queima mensurada; ausência de dados não significa caixa ilimitado.',
    pending: 'Informar saldo de caixa conciliado e mensurar o Burn Rate.',
  },
  {
    id: 'gross-margin',
    label: 'Margem Bruta',
    name: 'Receita após custos diretos',
    group: 'financial',
    description:
      '(Receita da plataforma − custos diretos de entrega) dividido pela receita da plataforma, em percentual. Exige classificar os custos diretos e seu responsável econômico. É diferente do take rate sobre o GMV.',
    pending: 'Classificar custos diretos e quais são suportados pela plataforma.',
  },
  {
    id: 'ebitda',
    label: 'EBITDA',
    name: 'Resultado operacional',
    group: 'financial',
    description:
      'Resultado antes de juros, impostos, depreciação e amortização. Requer classificação contábil de receitas e despesas; a parcela da plataforma nos splits, sozinha, não é EBITDA.',
    pending:
      'Classificar despesas operacionais, juros, tributos, depreciação e amortização.',
  },
] as const
